/* eslint-disable no-case-declarations */
import { kpiUtils } from '@replai-platform/sdk';
import { useState } from 'react';
import LineChart, { ChartValue } from '../../../LineChart/LineChart';
import LoadingBars from '../../../LoadingBars/LoadingBars';
import SelectInput, {
  SelectInputOption,
} from '../../../SelectInput/SelectInput';
import * as Styled from './styles';
import ButtonGroup from '../../../ButtonGroup';
export interface TagTrendsProps {
  availableKpis: SelectInputOption[];
  defaultKpi: SelectInputOption;
  onKpiChange: (index: number) => void;
  performanceChartData: ChartValue[];
  performanceChartIsLoading: boolean;
}

const GRADUALITY_OPTIONS = [
  { label: 'DAY', value: 'day' },
  { label: 'WEEK', value: 'week' },
  { label: 'MONTH', value: 'month' },
] as const;

type GradualityOption = (typeof GRADUALITY_OPTIONS)[number]['value'];

const TagTrends: React.VFC<TagTrendsProps> = ({
  availableKpis,
  defaultKpi,
  onKpiChange,
  performanceChartData,
  performanceChartIsLoading,
}) => {
  const [selectedKpi, setSelectedKpi] = useState(defaultKpi.title);
  const [graduality, setGraduality] = useState<GradualityOption>('day');

  const selectedGlobalKpi = `global${selectedKpi}`;
  const selectedTagKpi = `tag${selectedKpi}`;

  const hasTagValues = performanceChartData?.some(
    (data) => !!data[selectedTagKpi]
  );

  const lines = [
    {
      name: `${selectedKpi === 'spend' ? 'Total' : 'Avg'} performance`,
      accessor: selectedGlobalKpi,
    },
  ];
  if (hasTagValues) {
    lines.push({
      name: `Videos w/selected tags`,
      accessor: selectedTagKpi,
    });
  }

  const calculateDayData = () => performanceChartData;

  const calculateWeeklyAverage = (
    weeklyData: ChartValue[],
    weekIndex: number,
    daysInWeek: number
  ) => {
    const currGlobalKpiValue = weeklyData[weekIndex][
      selectedGlobalKpi
    ] as number;
    const currTagKpiValue = weeklyData[weekIndex][selectedTagKpi] as number;

    return {
      ...weeklyData[weekIndex],
      [selectedGlobalKpi]: currGlobalKpiValue / daysInWeek,
      [selectedTagKpi]: currTagKpiValue / daysInWeek,
    };
  };

  const calculateWeekData = () => {
    return performanceChartData.reduce(
      (weeklyData: ChartValue[], dailyData: ChartValue, index: number) => {
        const weekIndex = Math.floor(index / 7);

        if (!weeklyData[weekIndex]) {
          weeklyData[weekIndex] = {
            name: dailyData.name,
            [selectedGlobalKpi]: 0,
            [selectedTagKpi]: 0,
          };
        }
        const currentWeek = weeklyData[weekIndex];
        const currentGlobalKpi = currentWeek[selectedGlobalKpi] as number;
        const currentTagKpi = currentWeek[selectedTagKpi] as number;
        const dailyGlobalKpi = dailyData[selectedGlobalKpi] as number;
        const dailyTagKpi = dailyData[selectedTagKpi] as number;

        currentWeek[selectedGlobalKpi] = currentGlobalKpi + dailyGlobalKpi;
        currentWeek[selectedTagKpi] = currentTagKpi + dailyTagKpi;

        const currDaysInWeek = (index + 1) % 7;
        const isLastDayOfWeek = currDaysInWeek === 0;
        const isLastDay = index === performanceChartData.length - 1;

        if (isLastDayOfWeek || isLastDay) {
          const daysInWeek = isLastDayOfWeek ? 7 : currDaysInWeek;
          weeklyData[weekIndex] = calculateWeeklyAverage(
            weeklyData,
            weekIndex,
            daysInWeek
          );
        }

        return weeklyData;
      },
      []
    );
  };

  const calculateMonthData = () => {
    const groupedByMonth = performanceChartData.reduce(
      (monthlyData: ChartValue[], dayData: ChartValue) => {
        const date = new Date(dayData.name);
        const monthKey = `${date.getFullYear()}-${date.getMonth()}`;

        const existingMonth = monthlyData.find(
          (month) => month.monthKey === monthKey
        );
        const currDailyGlobalKpi = dayData[selectedGlobalKpi] as number;
        const currDailyTagKpi = dayData[selectedTagKpi] as number;

        if (!existingMonth) {
          const newMonth = {
            name: dayData.name,
            monthKey,
            [selectedGlobalKpi]: currDailyGlobalKpi,
            [selectedTagKpi]: currDailyTagKpi,
            daysInMonth: 1,
          };
          monthlyData.push(newMonth);
          return monthlyData;
        }

        (existingMonth[selectedGlobalKpi] as number) += currDailyGlobalKpi;
        (existingMonth[selectedTagKpi] as number) += currDailyTagKpi;
        (existingMonth.daysInMonth as number) += 1;

        return monthlyData;
      },
      []
    );

    return groupedByMonth.map((monthData) => ({
      name: monthData.name,
      [selectedGlobalKpi]:
        (monthData[selectedGlobalKpi] as number) /
        (monthData.daysInMonth as number),
      [selectedTagKpi]:
        (monthData[selectedTagKpi] as number) /
        (monthData.daysInMonth as number),
    }));
  };

  const chartData = () => {
    switch (graduality) {
      case 'day':
        return calculateDayData();
      case 'week':
        return calculateWeekData();
      case 'month':
        return calculateMonthData();
      default:
        const result: never = graduality;
        return result;
    }
  };

  const formatXAxisLabel = (v: number | string) => {
    return new Date(v).toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
    });
  };

  return (
    <div>
      <Styled.CardHeader>
        <Styled.TypographyClean type="text-md" fontWeight="medium">
          Performance over time
        </Styled.TypographyClean>
        <Styled.CardHeaderRight>
          <SelectInput
            defaultOption={defaultKpi}
            onChange={(index) => {
              setSelectedKpi(availableKpis[index].title);
              onKpiChange(index);
            }}
            options={availableKpis}
            placeholder="Select KPI"
          />
        </Styled.CardHeaderRight>
      </Styled.CardHeader>
      <Styled.CardChart data-test="overview-chart">
        {performanceChartIsLoading ? (
          <LoadingBars />
        ) : (
          <LineChart
            data-test="chart"
            data={chartData()}
            yValueFormatter={(v: number) =>
              kpiUtils.format(selectedKpi.toLowerCase(), v)
            }
            xValueFormatter={formatXAxisLabel}
            lines={lines}
          />
        )}
      </Styled.CardChart>
      <Styled.GradualityBox>
        <ButtonGroup.Container>
          {GRADUALITY_OPTIONS.map((option) => (
            <ButtonGroup.Button
              key={option.value}
              onClick={() => setGraduality(option.value)}
              color={option.value === graduality ? 'Blue' : 'BlueGray'}
              variant={option.value === graduality ? 'regular' : 'outlined'}
            >
              {option.label}
            </ButtonGroup.Button>
          ))}
        </ButtonGroup.Container>
      </Styled.GradualityBox>
    </div>
  );
};
export default TagTrends;
